/**
 * ゲームのスラッグを後から変える（2026-09-28 creator/slug-rename）の、画面から切り離した判断（node --test で確かめる・slugRename.test.ts）。
 * 画面は components/SlugSettings.tsx（ホスティングの画面の「URL スラッグ」のカード）。
 *
 * API の正: Core の core/slug-rename（指示書 docs/teams/creator/tasks/slug-rename.md の「API」= 案の段階で合意済み）。
 *   読む: GET /v1/projects/:id/slug・GET /v1/projects/:id/slug/check?slug=
 *   書く: POST /v1/projects/:id/slug { slug, redirectOldSlug, password? | reauthToken? }
 *         PATCH /v1/projects/:id/slug/old/:oldSlug { redirect, password? | reauthToken? }
 *   2026-09-28 Core の実装（core/slug-rename 9e5c9470・apps/server/src/modules/platform/slugRename.ts と README）に合わせた。案との違い:
 *   - 再認証は既存の共通の形（400 reauthentication_required / 401 reauthentication_failed・details = { hasPassword, providers }）
 *     = 部品（components/Reauth.tsx の run）がそのまま扱える。案の「403 reauth_required」の読み替えは要らなくなったので消した
 *   - URL は環境ごと（{ production: { url, hostUrl, pathUrl }, development: … }）・effects.customDomains は 'unchanged' の 1 語
 *   - 送信元の固定の理由に 'already_pinned'（前の変更で固定済み）が加わった・409 account_closed（閉鎖済みのアカウント）
 *
 * founder の決定（2026-09-28）で守ること:
 *   - 古い URL の転送は 1 つにつき月 $1（日割りで前払いから）。**既定の選択は無し**: 変えるときに本人が「残す / 残さない」を必ず選ぶ
 *     （料金が知らないうちに掛からないように）→ changeBody は選んでいないと例外を投げる・canConfirmChange は false
 *   - 転送は後から ON / OFF できる（古いスラッグは本人のために取っておかれる）
 *   - 金額は画面に直書きしない（サーバーの redirectChoices / redirectPricing の値を出す）。値が無いときは null = 金額なしの文言にする
 *
 * import は型だけ（ランタイムの import を持たない = node --experimental-strip-types でそのまま読める）。
 */
import type { ReauthProof } from './devOAuth';

/** サーバーと同じ規則（slugSuggestions.ts と同じ: 3〜58 文字・英小文字と数字・単独のハイフン・"--" 禁止） */
export const SLUG_RE = /^(?!.*--)[a-z0-9](?:[a-z0-9-]{1,56}[a-z0-9])$/;

/** 1 つの環境の URL（サーバーの hosting/paths.ts の hostingUrls）。url = 遊ぶ URL（配信ドメインが無いサーバーでは /play/… の形） */
export interface EnvUrls {
  url: string | null;
  hostUrl?: string | null;
  pathUrl?: string | null;
}

/** 環境ごとの URL */
export interface SlugUrls {
  production?: EnvUrls | null;
  development?: EnvUrls | null;
}

export interface OldSlug {
  slug: string;
  /** 古い URL を新しい URL へ転送しているか（ON = 月 $1・日割り） */
  redirect: boolean;
  retiredAt: string;
  urls?: SlugUrls;
}

export interface RedirectPricing {
  monthlyUsd: number;
  proration: 'daily' | string;
  per: 'old_slug' | string;
}

export interface MailSender {
  /** noreply@<slug>.<メールの配信ドメイン>。配信ドメインが無いサーバーでは null */
  address: string | null;
  /** 送信元に使う slug（固定されていれば変更前の名前） */
  slug?: string;
  /** 一度でもメールを送ったゲームは、送信元を変更前の名前に固定する（到達率のため・Core の ④） */
  pinned: boolean;
}

/** GET /v1/projects/:id/slug */
export interface SlugView {
  slug: string;
  urls?: SlugUrls;
  /** 次に変えられる時刻（null = 今すぐ変えられる） */
  nextChangeAt: string | null;
  changeIntervalDays: number;
  /** 直前の変更を 24 時間以内に取り消せるとき（回数に数えない） */
  undo: { slug: string; until: string } | null;
  oldSlugs: OldSlug[];
  mailSender: MailSender | null;
  redirectPricing: RedirectPricing | null;
}

export interface RedirectChoiceInfo {
  redirectOldSlug: boolean;
  monthlyUsd: number;
  proration?: string | null;
  effect?: string;
}

/** GET /v1/projects/:id/slug/check?slug= */
export interface SlugCheck {
  slug: string;
  available: boolean;
  error?: { code: string; message: string; details?: Record<string, unknown> };
  isUndo: boolean;
  countsTowardLimit: boolean;
  nextChangeAt: string | null;
  effects?: {
    oldSlug: string;
    oldUrls?: SlugUrls;
    newUrls?: SlugUrls;
    /** 独自ドメインは slug と無関係 = 常に 'unchanged'（サーバーの 1 語） */
    customDomains?: 'unchanged' | string;
    /** keptBecause: has_sent_mail = この変更で固定する / already_pinned = 前の変更で固定済み / null = 新しい名前に付いていく */
    mailSender?: { before: string | null; after: string | null; keptBecause: 'has_sent_mail' | 'already_pinned' | null };
    unsubscribeLinks?: 'keep_working' | string;
  };
  redirectChoices?: RedirectChoiceInfo[];
}

/** POST /v1/projects/:id/slug の応答 */
export interface SlugChangeResult {
  project: { id: string; slug: string };
  previousSlug: string;
  redirectOldSlug: boolean;
  /** 直前の変更の取り消しだったか（回数に数えない） */
  undo?: boolean;
  nextChangeAt: string | null;
  oldSlugs: OldSlug[];
  urls?: SlugUrls;
  mailSender?: MailSender | null;
}

/** 古いリンクの扱いの 2 択。null = まだ選んでいない（既定値は置かない = founder の決定） */
export type RedirectChoice = 'keep' | 'drop' | null;

export const slugPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/slug`;
export const slugCheckPath = (projectId: string, slug: string) => `${slugPath(projectId)}/check?slug=${encodeURIComponent(slug)}`;
export const oldSlugPath = (projectId: string, oldSlug: string) => `${slugPath(projectId)}/old/${encodeURIComponent(oldSlug)}`;

/** 入力の正規化（前後の空白を落とし小文字に。サーバーも小文字だけを受ける） */
export const normalizeSlug = (s: string) => s.trim().toLowerCase();

/** 確認の画面で「変更する」を押せるか: 使えるスラッグ + 2 択のどちらかを選んだ */
export function canConfirmChange(check: Pick<SlugCheck, 'available'> | null, choice: RedirectChoice): boolean {
  return !!check?.available && (choice === 'keep' || choice === 'drop');
}

/**
 * POST の本文。2 択を選んでいなければ投げる（画面のボタンは押せないが、念のため送る直前でも止める = 既定値で送らない）。
 * サーバーも redirectOldSlug が無いと 400 redirect_choice_required を返す（二重の守り）
 */
export function changeBody(slug: string, choice: RedirectChoice, proof: ReauthProof | Record<string, never>) {
  if (choice !== 'keep' && choice !== 'drop') throw new Error('redirect choice is required');
  return { slug, redirectOldSlug: choice === 'keep', ...proof };
}

/** PATCH の本文 */
export const redirectBody = (redirect: boolean, proof: ReauthProof | Record<string, never>) => ({ redirect, ...proof });

/**
 * 転送 1 つの月額（USD）。check の redirectChoices（redirectOldSlug: true の行）> GET slug の redirectPricing。どちらも無ければ null。
 * WHY 直書きしないか: 金額の正はサーバー（Money の料金表）。$1 を画面に書くと、値が変わったときに画面だけ古い金額を見せる
 */
export function redirectMonthlyUsd(check: Pick<SlugCheck, 'redirectChoices'> | null, view: Pick<SlugView, 'redirectPricing'> | null): number | null {
  const fromCheck = check?.redirectChoices?.find((c) => c.redirectOldSlug === true)?.monthlyUsd;
  if (typeof fromCheck === 'number' && Number.isFinite(fromCheck) && fromCheck >= 0) return fromCheck;
  const fromView = view?.redirectPricing?.monthlyUsd;
  if (typeof fromView === 'number' && Number.isFinite(fromView) && fromView >= 0) return fromView;
  return null;
}

/** 日割りか（案では 'daily'）。知らない値なら false = 「日割り」と言わない */
export function isDailyProration(check: Pick<SlugCheck, 'redirectChoices'> | null, view: Pick<SlugView, 'redirectPricing'> | null): boolean {
  const p = check?.redirectChoices?.find((c) => c.redirectOldSlug === true)?.proration ?? view?.redirectPricing?.proration;
  return p === 'daily';
}

/**
 * 再認証の action（「Google で確認」から戻ったときに続きを決める名前）。スラッグと選んだ 2 択を入れる =
 * 戻ってきたら同じ内容の確認画面を開き直せる（途中の state はプロバイダの画面へ移ると消えるため）。
 * スラッグは SLUG_RE で ':' を含まないので区切りに使える
 */
export const slugChangeAction = (slug: string, choice: 'keep' | 'drop') => `slug:change:${slug}:${choice}`;
export const slugRedirectAction = (oldSlug: string, on: boolean) => `slug:redirect:${oldSlug}:${on ? 'on' : 'off'}`;

export type ParsedSlugAction = { kind: 'change'; slug: string; choice: 'keep' | 'drop' } | { kind: 'redirect'; oldSlug: string; on: boolean };

/** action を読み戻す。別の画面の action・壊れた値は null */
export function parseSlugAction(action: string | null | undefined): ParsedSlugAction | null {
  if (!action) return null;
  const c = /^slug:change:([a-z0-9-]+):(keep|drop)$/.exec(action);
  if (c && SLUG_RE.test(c[1]!)) return { kind: 'change', slug: c[1]!, choice: c[2] as 'keep' | 'drop' };
  const r = /^slug:redirect:([a-z0-9-]+):(on|off)$/.exec(action);
  if (r && SLUG_RE.test(r[1]!)) return { kind: 'redirect', oldSlug: r[1]!, on: r[2] === 'on' };
  return null;
}

/** 取り消し（undo）が今も使えるか（until を過ぎたら出さない） */
export function undoAvailable(view: Pick<SlugView, 'undo'> | null, now = Date.now()): boolean {
  const u = view?.undo;
  return !!u && Date.parse(u.until) > now;
}

/** 今すぐ変えられるか（nextChangeAt が無い・過ぎた）。undo は別（回数に数えないので、待つ間でも出す） */
export function canChangeNow(view: Pick<SlugView, 'nextChangeAt'> | null, now = Date.now()): boolean {
  const n = view?.nextChangeAt;
  return !n || Date.parse(n) <= now;
}

/**
 * 送信元のメールアドレスの見せ方:
 *   'pinned'  … 一度でもメールを送ったゲーム = 元の名前のまま（keptBecause: has_sent_mail・前の変更で固定済みなら already_pinned）。「noreply@old.<C> のまま（到達率を守るため）」
 *   'changes' … 新しいスラッグの名前に変わる（before → after）
 *   null      … 送信元が無い（メールを使っていない）・変わらない = 何も出さない
 */
export function mailSenderChange(check: Pick<SlugCheck, 'effects'> | null): { kind: 'pinned'; address: string } | { kind: 'changes'; before: string; after: string } | null {
  const m = check?.effects?.mailSender;
  if (!m || !m.before) return null;
  if (m.keptBecause === 'has_sent_mail' || m.keptBecause === 'already_pinned') return { kind: 'pinned', address: m.before };
  if (!m.after || m.after === m.before) return null;
  return { kind: 'changes', before: m.before, after: m.after };
}

/** URL の組から表示する行（production・development の順。無いものは出さない） */
export function urlRows(urls: SlugUrls | undefined | null): Array<{ env: 'production' | 'development'; url: string }> {
  const out: Array<{ env: 'production' | 'development'; url: string }> = [];
  for (const env of ['production', 'development'] as const) {
    const u = urls?.[env];
    const url = u?.url ?? u?.hostUrl ?? u?.pathUrl ?? null;
    if (url) out.push({ env, url });
  }
  return out;
}

/** 429 slug_change_too_soon の次に変えられる時刻（details.nextChangeAt）。無ければ null */
export function tooSoonUntil(err: { code?: string; details?: Record<string, unknown> } | null | undefined): string | null {
  if (!err || err.code !== 'slug_change_too_soon') return null;
  const n = err.details?.nextChangeAt;
  return typeof n === 'string' && !Number.isNaN(Date.parse(n)) ? n : null;
}

/** 画面に並べる候補の上限（作成の画面の slugSuggestions.ts と同じ 3 つ） */
const MAX_SUGGESTIONS = 3;

/**
 * check の「使えない」の候補（error.details.suggestions）。slug_taken / slug_reserved のときサーバーが空いている候補を返す（案）。
 * 規則に合わない値・重複は捨てる（壊れた応答でも画面を壊さない）。POST の 409 / 400 の候補は slugSuggestions.ts の slugSuggestionsFrom
 */
export function checkSuggestions(check: Pick<SlugCheck, 'available' | 'error'> | null): string[] {
  if (!check || check.available) return [];
  const list = check.error?.details?.suggestions;
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const s of list) {
    if (typeof s === 'string' && SLUG_RE.test(s) && !out.includes(s)) out.push(s);
    if (out.length >= MAX_SUGGESTIONS) break;
  }
  return out;
}

/**
 * 設定の画面の説明の文（変えられる間隔）。2026-10-04 に間隔が 1 日になった（サーバーの SLUG_CHANGE_INTERVAL_DAYS）ので、
 * 「once every 1 days」と出さないよう 1 日のときは別の文（slug.leadDaily）。日数はサーバーの changeIntervalDays（直書きしない）
 */
export function slugLeadMessage(days: number, slug: string): { key: 'slug.lead' | 'slug.leadDaily'; vars: Record<string, string> } {
  if (days === 1) return { key: 'slug.leadDaily', vars: { slug } };
  return { key: 'slug.lead', vars: { slug, days: String(days) } };
}
